// LocationManagementPage.jsx - İl/İlçe Yönetim Sayfası
import React, { useState, useEffect } from 'react';
import { 
  FaCity, FaPlus, FaEdit, FaTrash, FaSearch, 
  FaMapMarkerAlt, FaFilter, FaFileExport,
  FaChevronDown, FaChevronUp, FaUsers, FaSync
} from 'react-icons/fa';
import axiosConfig from '../../../api/axiosConfig';

const LocationManagementPage = () => {
  // State tanımlamaları
  const [activeTab, setActiveTab] = useState('sehirler'); // 'sehirler' veya 'ilceler'
  const [sehirler, setSehirler] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [selectedSehir, setSelectedSehir] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    il_adi: '',
    plaka_kodu: '',
    alan_kodu: '',
    nufus: '',
    bolge: '',
    // İlçe için
    sehir_id: '',
    ilce_adi: '',
    ilce_kodu: ''
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedSehir, setExpandedSehir] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Bölgeler listesi
  const bolgeler = [
    'Marmara',
    'Ege', 
    'Akdeniz',
    'Karadeniz',
    'İç Anadolu',
    'Doğu Anadolu',
    'Güneydoğu Anadolu'
  ];

  // Component mount
  useEffect(() => {
    if (activeTab === 'sehirler') {
      fetchSehirler();
    } else {
      fetchAllIlceler();
    }
  }, [activeTab, currentPage, searchTerm]);

  // Şehirleri getir
  const fetchSehirler = async () => {
    setLoading(true);
    try {
      const response = await axiosConfig.get('/admin/location/admin/sehirler', {
        params: {
          page: currentPage,
          limit: 20,
          search: searchTerm
        }
      });
      setSehirler(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setTotalCount(response.data.pagination.totalCount);
    } catch (error) {
      console.error('Şehirler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  // Tüm ilçeleri getir
  const fetchAllIlceler = async () => {
    setLoading(true);
    try {
      const response = await axiosConfig.get('/admin/location/admin/ilceler', {
        params: {
          page: currentPage,
          limit: 50,
          search: searchTerm
        }
      });
      setIlceler(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setTotalCount(response.data.pagination.totalCount);
    } catch (error) {
      console.error('İlçeler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  // Belirli şehrin ilçelerini getir
  const fetchSehirIlceleri = async (sehirId) => {
    try {
      const response = await axiosConfig.get(`/admin/location/admin/sehir/${sehirId}/ilceler`);
      return response.data.data;
    } catch (error) {
      console.error('İlçeler yüklenemedi:', error);
      return [];
    }
  };

  // Form aç/kapa
  const openForm = (item = null) => {
    if (item) {
      setEditingItem(item);
      if (activeTab === 'sehirler') {
        setFormData({
          il_adi: item.il_adi,
          plaka_kodu: item.plaka_kodu,
          alan_kodu: item.alan_kodu || '',
          nufus: item.nufus || '',
          bolge: item.bolge || ''
        });
      } else {
        setFormData({
          sehir_id: item.sehir_id,
          ilce_adi: item.ilce_adi,
          ilce_kodu: item.ilce_kodu || '',
          nufus: item.nufus || ''
        });
      }
    } else {
      setEditingItem(null);
      setFormData({
        il_adi: '',
        plaka_kodu: '',
        alan_kodu: '',
        nufus: '',
        bolge: '',
        sehir_id: selectedSehir?.id || '',
        ilce_adi: '',
        ilce_kodu: ''
      });
    }
    setShowFormModal(true);
  };

  const closeForm = () => {
    setShowFormModal(false);
    setEditingItem(null);
    setFormData({
      il_adi: '',
      plaka_kodu: '',
      alan_kodu: '',
      nufus: '',
      bolge: '',
      sehir_id: '',
      ilce_adi: '',
      ilce_kodu: ''
    });
  };

  // Form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (activeTab === 'sehirler') {
        if (editingItem) {
          await axiosConfig.put(`/admin/location/admin/sehirler/${editingItem.id}`, formData);
        } else {
          await axiosConfig.post('/admin/location/admin/sehirler', formData);
        }
        fetchSehirler();
      } else {
        if (editingItem) {
          await axiosConfig.put(`/admin/location/admin/ilceler/${editingItem.id}`, formData);
        } else {
          await axiosConfig.post('/admin/location/admin/ilceler', formData);
        }
        fetchAllIlceler();
      }
      closeForm();
    } catch (error) {
      console.error('İşlem başarısız:', error);
      alert('İşlem sırasında bir hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  // Silme işlemi
  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) return;
    
    setLoading(true);
    try {
      if (activeTab === 'sehirler') {
        await axiosConfig.delete(`/admin/location/admin/sehirler/${id}`);
        fetchSehirler();
      } else {
        await axiosConfig.delete(`/admin/location/admin/ilceler/${id}`);
        fetchAllIlceler();
      }
    } catch (error) {
      console.error('Silme işlemi başarısız:', error);
      alert('Silme işlemi sırasında bir hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  // Toplu silme
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      alert('Lütfen silmek için kayıt seçin!');
      return;
    }

    if (!window.confirm(`${selectedItems.length} kayıt silinecek. Emin misiniz?`)) return;

    setLoading(true);
    try {
      if (activeTab === 'sehirler') {
        await axiosConfig.post('/admin/location/admin/sehirler/bulk-delete', { ids: selectedItems });
        fetchSehirler();
      } else {
        await axiosConfig.post('/admin/location/admin/ilceler/bulk-delete', { ids: selectedItems });
        fetchAllIlceler();
      }
      setSelectedItems([]);
    } catch (error) {
      console.error('Toplu silme başarısız:', error);
      alert('Toplu silme sırasında bir hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  // Şehir genişlet/daralt
  const toggleSehirExpand = async (sehir) => {
    if (expandedSehir === sehir.id) {
      setExpandedSehir(null);
    } else {
      setExpandedSehir(sehir.id);
      const ilcelerData = await fetchSehirIlceleri(sehir.id);
      setSehirler(prev => prev.map(s => 
        s.id === sehir.id ? { ...s, ilceler: ilcelerData } : s
      ));
    }
  };

  // Checkbox işlemleri
  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const currentData = activeTab === 'sehirler' ? sehirler : ilceler;
    if (selectedItems.length === currentData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(currentData.map(item => item.id));
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <FaCity className="text-blue-600" />
          İl / İlçe Yönetimi
        </h1>
        <p className="text-gray-600 mt-2">Türkiye'deki il ve ilçeleri yönetin</p>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Toplam İl</p>
              <p className="text-2xl font-bold text-gray-800">81</p>
            </div>
            <FaCity className="text-3xl text-blue-500 opacity-30" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Toplam İlçe</p>
              <p className="text-2xl font-bold text-gray-800">973</p>
            </div>
            <FaMapMarkerAlt className="text-3xl text-green-500 opacity-30" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Ortalama İlçe</p>
              <p className="text-2xl font-bold text-gray-800">12</p>
            </div>
            <FaUsers className="text-3xl text-purple-500 opacity-30" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Son Güncelleme</p>
              <p className="text-sm font-bold text-gray-800">Bugün</p>
            </div>
            <FaSync className="text-3xl text-orange-500 opacity-30" />
          </div>
        </div>
      </div>

      {/* Tab Menu */}
      <div className="bg-white rounded-lg shadow mb-4">
        <div className="flex border-b">
          <button
            onClick={() => {
              setActiveTab('sehirler');
              setCurrentPage(1);
              setSelectedItems([]);
            }}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'sehirler'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <FaCity className="inline mr-2" />
            Şehirler
          </button>
          <button
            onClick={() => {
              setActiveTab('ilceler');
              setCurrentPage(1);
              setSelectedItems([]);
            }}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'ilceler'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <FaMapMarkerAlt className="inline mr-2" />
            İlçeler
          </button>
        </div>
      </div>

      {/* Araç Çubuğu */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => openForm()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <FaPlus />
              {activeTab === 'sehirler' ? 'Yeni Şehir' : 'Yeni İlçe'}
            </button>
            
            {selectedItems.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <FaTrash />
                Seçilenleri Sil ({selectedItems.length})
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            
            <button
              onClick={() => {
                setCurrentPage(1);
                if (activeTab === 'sehirler') {
                  fetchSehirler();
                } else {
                  fetchAllIlceler();
                }
              }}
              className="p-2 border rounded-lg hover:bg-gray-50"
            >
              <FaSync className={loading ? 'animate-spin' : ''} />
            </button>
            
            <button className="p-2 border rounded-lg hover:bg-gray-50">
              <FaFileExport />
            </button>
          </div>
        </div>
      </div>

      {/* Tablo */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={
                      selectedItems.length > 0 && 
                      selectedItems.length === (activeTab === 'sehirler' ? sehirler : ilceler).length
                    }
                  />
                </th>
                {activeTab === 'sehirler' ? (
                  <>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">#</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">İl Adı</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Plaka</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Alan Kodu</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Bölge</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Nüfus</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">İlçe Sayısı</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">İşlemler</th>
                  </>
                ) : (
                  <>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">#</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">İlçe Adı</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">İl</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">İlçe Kodu</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Nüfus</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">İşlemler</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                    Yükleniyor...
                  </td>
                </tr>
              ) : activeTab === 'sehirler' ? (
                sehirler.map((sehir, index) => (
                  <React.Fragment key={sehir.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(sehir.id)}
                          onChange={() => handleSelectItem(sehir.id)}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">{index + 1}</td>
                      <td className="px-4 py-3 font-medium">
                        <button
                          onClick={() => toggleSehirExpand(sehir)}
                          className="flex items-center gap-2 hover:text-blue-600"
                        >
                          {expandedSehir === sehir.id ? <FaChevronUp /> : <FaChevronDown />}
                          {sehir.il_adi}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {sehir.plaka_kodu}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{sehir.alan_kodu}</td>
                      <td className="px-4 py-3 text-sm">{sehir.bolge}</td>
                      <td className="px-4 py-3 text-sm">{sehir.nufus}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                          {sehir.ilce_sayisi} ilçe
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openForm(sehir)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(sehir.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedSehir === sehir.id && sehir.ilceler && (
                      <tr>
                        <td colSpan="9" className="px-8 py-4 bg-gray-50">
                          <div className="grid grid-cols-3 gap-2">
                            {sehir.ilceler.map(ilce => (
                              <div key={ilce.id} className="flex items-center justify-between bg-white p-2 rounded border">
                                <span className="text-sm">{ilce.ilce_adi}</span>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => {
                                      setActiveTab('ilceler');
                                      openForm(ilce);
                                    }}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  >
                                    <FaEdit className="text-xs" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(ilce.id)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                  >
                                    <FaTrash className="text-xs" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                ilceler.map((ilce, index) => (
                  <tr key={ilce.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(ilce.id)}
                        onChange={() => handleSelectItem(ilce.id)}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">{index + 1}</td>
                    <td className="px-4 py-3 font-medium">{ilce.ilce_adi}</td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{ilce.il_adi}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">{ilce.ilce_kodu}</td>
                    <td className="px-4 py-3 text-sm">{ilce.nufus}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openForm(ilce)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(ilce.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="px-4 py-3 border-t flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Toplam {totalCount} kayıt
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Önceki
            </button>
            <span className="px-3 py-1">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Sonraki
            </button>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingItem 
                  ? (activeTab === 'sehirler' ? 'Şehir Düzenle' : 'İlçe Düzenle')
                  : (activeTab === 'sehirler' ? 'Yeni Şehir Ekle' : 'Yeni İlçe Ekle')
                }
              </h3>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              {activeTab === 'sehirler' ? (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      İl Adı <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.il_adi}
                      onChange={(e) => setFormData({...formData, il_adi: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Plaka Kodu <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.plaka_kodu}
                        onChange={(e) => setFormData({...formData, plaka_kodu: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        maxLength="3"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alan Kodu
                      </label>
                      <input
                        type="text"
                        value={formData.alan_kodu}
                        onChange={(e) => setFormData({...formData, alan_kodu: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        maxLength="5"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bölge
                    </label>
                    <select
                      value={formData.bolge}
                      onChange={(e) => setFormData({...formData, bolge: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seçiniz</option>
                      {bolgeler.map(bolge => (
                        <option key={bolge} value={bolge}>{bolge}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nüfus
                    </label>
                    <input
                      type="text"
                      value={formData.nufus}
                      onChange={(e) => setFormData({...formData, nufus: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Şehir <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.sehir_id}
                      onChange={(e) => setFormData({...formData, sehir_id: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Seçiniz</option>
                      {sehirler.map(sehir => (
                        <option key={sehir.id} value={sehir.id}>
                          {sehir.il_adi}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      İlçe Adı <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.ilce_adi}
                      onChange={(e) => setFormData({...formData, ilce_adi: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        İlçe Kodu
                      </label>
                      <input
                        type="text"
                        value={formData.ilce_kodu}
                        onChange={(e) => setFormData({...formData, ilce_kodu: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nüfus
                      </label>
                      <input
                        type="text"
                        value={formData.nufus}
                        onChange={(e) => setFormData({...formData, nufus: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </>
              )}
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Kaydediliyor...' : (editingItem ? 'Güncelle' : 'Kaydet')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationManagementPage;